import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import PageLayout from "components/layouts/PageLayout";
import AutomationExecution from "lib/api/AutomationExecution";
import {
  StatusBadge,
  ActionLabel,
  formatDateTime,
  formatDuration,
  formatMb,
  awsConsoleUrl,
  automationStyles,
} from "components/automation/shared";

const AUTO_REFRESH_MS = 15000;
// Steps worth calling out to support; they get a highlighted row.
const KEY_STEPS = ["MigrateTenantData"];

function ConsoleLink({ href, children = "AWS Console ↗" }) {
  if (!href) return null;
  return (
    <a className="automation-btn" href={href} target="_blank" rel="noopener noreferrer" title="Opens in the AWS console (AWS sign-in required)">
      {children}
    </a>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ minWidth: 180 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 14, color: "#111827" }}>{children}</div>
    </div>
  );
}

export default function AutomationExecutionDetail(props) {
  const executionId = props.match.params.execution_id;
  const [execution, setExecution] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setExecution(await AutomationExecution.load(executionId));
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [executionId]);

  useEffect(() => {
    load();
  }, [load]);

  // Keep a running execution's step timeline live until it finishes.
  const running = execution?.status_group === "in_progress";
  useEffect(() => {
    if (!running) return undefined;
    const timer = setInterval(load, AUTO_REFRESH_MS);
    return () => clearInterval(timer);
  }, [running, load]);

  return (
    <PageLayout>
      <style>{automationStyles}</style>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <h1>{execution?.action_label || "Automation Run"}</h1>
        <div style={{ display: "flex", gap: 8 }}>
          <Link className="automation-btn" to="/automations">← All runs</Link>
          <ConsoleLink href={execution && awsConsoleUrl(execution.execution_id)}>View in AWS Console ↗</ConsoleLink>
          <button className="automation-btn" onClick={load} disabled={loading}>
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
      </div>
      <div className="hr hr--darkest"></div>

      {error && (
        <div style={{ padding: 10, margin: "10px 0", borderRadius: 6, background: "#fef2f2", color: "#991b1b", fontSize: 13 }}>
          {error}
        </div>
      )}

      {execution && (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 24, padding: 16, margin: "12px 0", background: "#ffffff", borderRadius: 8, outline: "1px solid #e5e7eb" }}>
            <Field label="Action">
              <ActionLabel action={execution.action} label={execution.action_label} />
            </Field>
            <Field label="Status">
              <StatusBadge group={execution.status_group} status={execution.status} />
            </Field>
            <Field label="Client">
              {execution.client ? (
                <Link to={`/client/${execution.client.uuid}`}>{execution.client.name}</Link>
              ) : (
                <span className="automation-muted">No matching client</span>
              )}
            </Field>
            <Field label="License #">{execution.license_number || "—"}</Field>
            {execution.target_user && (
              <Field label="Cloud User">
                <span style={{ fontFamily: "monospace" }}>{execution.target_user}</span>
              </Field>
            )}
            {execution.action === "migration" && (
              <>
                <Field label="Data Migrated">
                  {execution.migration_data ? (
                    <Link to={`/data-sync/${execution.client.uuid}`}>
                      {formatMb(execution.migration_data.total_data_mb)}
                    </Link>
                  ) : (
                    <span className="automation-muted">No Data Sync record</span>
                  )}
                </Field>
                {execution.migration_data?.file_count != null && (
                  <Field label="Files">{execution.migration_data.file_count.toLocaleString()}</Field>
                )}
                {execution.migration_data?.finalization_status && (
                  <Field label="Data Sync Finalization">{execution.migration_data.finalization_status}</Field>
                )}
              </>
            )}
            <Field label="Started">{formatDateTime(execution.started_at)}</Field>
            <Field label="Ended">{formatDateTime(execution.ended_at)}</Field>
            <Field label="Duration">{formatDuration(execution.duration_seconds)}</Field>
            <Field label="Triggered By">
              <span style={{ fontSize: 12 }}>{execution.triggered_by || "—"}</span>
            </Field>
            <Field label="Execution ID">
              <span style={{ fontFamily: "monospace", fontSize: 12 }}>{execution.execution_id}</span>
            </Field>
          </div>

          {execution.failure_message && (
            <div style={{ padding: 12, marginBottom: 12, borderRadius: 8, background: "#fef2f2", outline: "1px solid #fecaca" }}>
              <div style={{ fontWeight: 600, color: "#991b1b", marginBottom: 6 }}>Failure</div>
              <pre style={{ whiteSpace: "pre-wrap", fontSize: 12, color: "#7f1d1d", margin: 0 }}>
                {execution.failure_message}
              </pre>
            </div>
          )}

          <div style={{ display: "flex", alignItems: "baseline", gap: 10, margin: "16px 0 8px" }}>
            <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>Steps</h2>
            {execution.skipped_step_count > 0 && (
              <span className="automation-muted">
                {execution.skipped_step_count} step{execution.skipped_step_count !== 1 ? "s" : ""} not run
                {execution.status_group === "in_progress" ? " yet" : ""} (other mode or skipped)
              </span>
            )}
          </div>
          <div style={{ background: "#ffffff", borderRadius: 8, outline: "1px solid #e5e7eb", overflow: "auto" }}>
            <table className="automation-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Step</th>
                  <th>Status</th>
                  <th>Started</th>
                  <th>Duration</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {execution.steps.map((step, i) => (
                  <tr
                    key={`${step.name}-${i}`}
                    style={KEY_STEPS.includes(step.name) ? { background: "#faf5ff", boxShadow: "inset 3px 0 0 #a855f7" } : undefined}
                  >
                    <td className="automation-muted">{i + 1}</td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{step.name}</div>
                      {step.failure_message && (
                        <pre style={{ whiteSpace: "pre-wrap", fontSize: 12, color: "#991b1b", margin: "4px 0 0", maxWidth: 640 }}>
                          {step.failure_message}
                        </pre>
                      )}
                    </td>
                    <td>
                      <StatusBadge group={step.status_group} status={step.status} />
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>{formatDateTime(step.started_at)}</td>
                    <td style={{ whiteSpace: "nowrap" }}>{formatDuration(step.duration_seconds)}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <ConsoleLink
                        href={step.step_execution_id ? awsConsoleUrl(execution.execution_id, step) : null}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </PageLayout>
  );
}
